import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bot, Brain, Clock, Pencil, Plug, Settings2, Sparkles, Wrench } from 'lucide-react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useAdminTools, useAgentDetail, useSkills, useTools } from '../../hooks';
import memoryService from '../../services/memoryService';
import { formatDate } from '../../helpers/common';
import { formatSkillName, getSkillId, normalizeSkillsList } from '../../utils/skillsUtils';
import { ReponseMenu, StyleItem } from '../SideBar/SideBar.styles';
import { MenuBtn } from './AgentProfile.style';
import Loaders from '../Loaders';

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    // Legacy SVG uploads are served by S3 as binary/octet-stream and won't render in <img>.
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:')) return icon;
  return `data:image/png;base64,${icon}`;
}

function normalizeIdList(value) {
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        return normalizeIdList(JSON.parse(trimmed));
      } catch {
        // fall through
      }
    }
    return trimmed
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.flatMap(item => {
      if (item == null || item === '') return [];
      if (typeof item === 'string' || typeof item === 'number') return [String(item)];
      if (typeof item === 'object') {
        const id = item.mcp_id || item.tool_id || item.skill_id || item.id || item.value;
        return id != null && id !== '' ? [String(id)] : [];
      }
      return [];
    });
  }
  return [];
}

const AgentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: agentData, isFetching } = useAgentDetail(id);
  const detail = agentData?.detail;

  const { fetch } = useContextHook(AuthContext, v => ({ fetch: v.fetch }));
  const { _data: memoryData } = memoryService.GetMemoryList(id, fetch);
  const memories = memoryData?.results?.results || [];

  const { data: userToolsData } = useTools('user_created');
  const { data: adminToolsData } = useAdminTools();
  const { data: skillsData } = useSkills();

  const allTools = useMemo(
    () => [...(userToolsData?.tools || []), ...(adminToolsData?.tools || [])],
    [userToolsData, adminToolsData],
  );

  const toolIds = useMemo(() => new Set(normalizeIdList(detail?.tools)), [detail]);
  const agentTools = useMemo(
    () => allTools.filter(tool => toolIds.has(String(tool?.tool_id))),
    [allTools, toolIds],
  );

  const agentMcps = useMemo(() => {
    const ids = normalizeIdList(
      detail?.mcps ?? detail?.mcp ?? detail?.mcp_ids ?? agentData?.mcps ?? agentData?.mcp,
    );
    return ids.map(mcpId => ({ mcp_id: mcpId, mcp_name: mcpId }));
  }, [detail, agentData]);

  const agentSkills = useMemo(() => {
    const skillIds = new Set(normalizeIdList(detail?.skills));
    if (skillIds.size === 0) return [];
    const catalog = normalizeSkillsList(skillsData);
    const matched = catalog.filter(skill => skillIds.has(String(getSkillId(skill) || '')));
    if (matched.length > 0) return matched;
    return [...skillIds].map(id => ({ skill_id: id, name: id }));
  }, [detail, skillsData]);

  if (isFetching) {
    return <Loaders pageLoader />;
  }

  const modelId = detail?.model_id || '';
  const provider = modelId.includes('gpt') ? 'OpenAI' : modelId.split('/')[0] || '—';
  const responseModel = modelId.includes('gpt') ? modelId : modelId.split('/')[1] || modelId;
  const iconSrc = resolveIconSrc(detail?.icon);
  const memoryEnabled = detail?.enable_user_memories;

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <button
        type="button"
        onClick={() => navigate('/agents')}
        className="flex items-center gap-2 text-sm text-muted hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Agents
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start gap-5 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {iconSrc ? (
            <img src={iconSrc} alt={detail?.agent_name} className="w-full h-full object-cover" />
          ) : (
            <Bot className="w-8 h-8 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-ink mb-2 font-serif">{detail?.agent_name}</h1>
          {detail?.description && <p className="text-muted mb-3 max-w-2xl">{detail.description}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-dim">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Agent ID
            </span>
            <span className="font-mono text-muted break-all">{id}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/edit-agent/${id}`)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-shell-card-border text-sm text-ink hover:border-muted-faint flex-shrink-0">
          <Pencil className="w-4 h-4" /> Edit
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings2 className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-semibold text-ink">Instructions</h3>
            </div>
            <p
              className="text-sm text-muted leading-relaxed whitespace-pre-line"
              dangerouslySetInnerHTML={{
                __html: detail?.instructions?.replace(/\r\n/g, '<br>') || 'No instructions provided.',
              }}
            />
          </div>

          {memoryEnabled && memories.length > 0 && (
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  <h3 className="text-sm font-semibold text-ink">Memory</h3>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-ink/5 text-muted">
                  {memories.length} items
                </span>
              </div>
              <div className="space-y-3">
                {memories.slice(0, 3).map((data, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg bg-shell border border-shell-card-border">
                    <div className="min-w-0">
                      <p className="text-sm text-ink break-words">{data?.memory}</p>
                      <p className="text-xs text-muted-dim mt-1">{formatDate(data?.created_at)}</p>
                    </div>
                    {data?.categories?.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          {data.categories[0]}
                        </span>
                        {data.categories.length > 1 && (
                          <ReponseMenu
                            menuButton={
                              <MenuBtn>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-ink/5 text-muted">
                                  + {data.categories.length - 1} more
                                </span>
                              </MenuBtn>
                            }>
                            {data.categories.slice(1).map((item, i) => (
                              <StyleItem key={i}>
                                <div className="text">{item}</div>
                              </StyleItem>
                            ))}
                          </ReponseMenu>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-ink mb-4">Configuration</h3>
            <div className="divide-y divide-shell-card-border">
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-muted">Provider</span>
                <span className="text-sm text-ink font-medium">{provider}</span>
              </div>
              <div className="flex items-center justify-between py-2.5 gap-3">
                <span className="text-sm text-muted">Response Model</span>
                <span className="text-sm text-ink font-medium text-right break-all">{responseModel}</span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-muted">Memory</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${
                    memoryEnabled
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : 'bg-ink/5 text-muted border-shell-card-border'
                  }`}>
                  {memoryEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {agentTools.length > 0 && (
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold text-ink">Tools & Integrations</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {agentTools.map((data, index) => (
                  <span
                    key={data?.tool_id || index}
                    className="px-2.5 py-1 rounded-full text-xs bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">
                    {data?.tool_name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {agentMcps.length > 0 && (
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Plug className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold text-ink">MCP Servers</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {agentMcps.map((data, index) => (
                  <span
                    key={data?.mcp_id || data?.id || index}
                    className="px-2.5 py-1 rounded-full text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/20">
                    {data?.mcp_name || data?.name || data?.mcp_id || data?.id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {agentSkills.length > 0 && (
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold text-ink">Skills</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {agentSkills.map((data, index) => (
                  <span
                    key={getSkillId(data) || index}
                    className="px-2.5 py-1 rounded-full text-xs bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">
                    {formatSkillName(data?.name || data?.skill_name || getSkillId(data))}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentProfile;
